import pipefitterApi from '../../../services/pipefitterApi';
import { sequenceStatusErrorMessages } from '../constants/configurationConstants';

/** Backend: Pending | Started | InProgress | Completed | ErrorOccurred */
export type MeanMaxCalculationStatus =
  | 'Pending'
  | 'Started'
  | 'InProgress'
  | 'Completed'
  | 'ErrorOccurred'
  | string;

export type ProjectInformation = {
  projectNumber?: string;
  projectName?: string;
  targetNumberOfPipes?: number | null;
  targetNumberOfPipesEnds?: number | null;
  meanMaxCalculationStatus?: MeanMaxCalculationStatus | null;
  [key: string]: unknown;
};

export function isMeanMaxReadyForReports(
  status: MeanMaxCalculationStatus | null | undefined
): boolean {
  return String(status || '').trim().toLowerCase() === 'completed';
}

/** True when mean/max is Started or InProgress — do not kick off another run. */
export function isMeanMaxCalculationRunning(
  status: MeanMaxCalculationStatus | null | undefined
): boolean {
  const normalized = String(status || '').trim().toLowerCase();
  return normalized === 'started' || normalized === 'inprogress';
}

export function meanMaxBlocksReportGeneration(
  status: MeanMaxCalculationStatus | null | undefined
): boolean {
  return !isMeanMaxReadyForReports(status);
}

export const MEAN_MAX_PENDING_REPORT_MESSAGE =
  sequenceStatusErrorMessages.meanMaxPendingError;

/**
 * GET `/v1/project/get-project-information/:projectNumber`
 */
export async function fetchProjectInformation(
  projectNumber: string
): Promise<ProjectInformation | null> {
  const trimmed = projectNumber?.trim();
  if (!trimmed) return null;

  const { data } = await pipefitterApi.get(
    `/project/get-project-information/${encodeURIComponent(trimmed)}`
  );

  if (data?.status !== 200) {
    throw new Error(data?.message || 'Failed to fetch project information');
  }

  const payload = data?.data;
  if (!payload || typeof payload !== 'object') return null;

  // Some list-shaped responses may wrap the project.
  if (Array.isArray(payload.projectList) && payload.projectList[0]) {
    return payload.projectList[0] as ProjectInformation;
  }
  return payload as ProjectInformation;
}
